import { Part, Profile } from '../features/profile/types';
import { isVisible } from './profileHelper';

export type PermissionCode = string;
export type PermissionFallbackMode = 'never' | 'if-no-permission-codes' | 'always';

export interface PermissionFallbackOptions {
    fallbackPart?: Part | null;
    fallbackParts?: Part[];
    fallbackMode?: PermissionFallbackMode;
}

export interface SectionVisibilityConfig extends PermissionFallbackOptions {
    permissionCodes?: PermissionCode[];
}

const DEFAULT_PERMISSION_FALLBACK_MODE: PermissionFallbackMode = 'never';
const DEFAULT_SECTION_FALLBACK_MODE: PermissionFallbackMode = 'always';

export function getPermissionCodes(profile?: Profile | null): Set<PermissionCode> {
    if (!profile?.permissions?.length) {
        return new Set<PermissionCode>();
    }

    return new Set(
        profile.permissions
            .map((permission) => (permission.code ?? permission.name)?.trim())
            .filter((code): code is PermissionCode => Boolean(code))
    );
}

export function normalizePermissionCode(permissionCode: PermissionCode): string {
    return permissionCode.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

export function hasPermissionCode(permissionCodes: Set<PermissionCode>, permissionCode: PermissionCode): boolean {
    const normalizedPermissionCode = normalizePermissionCode(permissionCode);

    return permissionCodes.has(permissionCode)
        || Array.from(permissionCodes).some((code) => normalizePermissionCode(code) === normalizedPermissionCode);
}

export function hasPermission(
    profile: Profile | null | undefined,
    permissionCode: PermissionCode,
    options?: PermissionFallbackOptions
): boolean {
    if (!permissionCode.trim()) {
        return false;
    }

    const permissionCodes = getPermissionCodes(profile);
    if (hasPermissionCode(permissionCodes, permissionCode)) {
        return true;
    }

    return shouldUsePartFallback(permissionCodes, options)
        ? hasLegacyPartAccess(profile, options)
        : false;
}

export function hasAnyPermission(
    profile: Profile | null | undefined,
    permissionCodes: PermissionCode[],
    options?: PermissionFallbackOptions
): boolean {
    const normalizedCodes = permissionCodes
        .map((permissionCode) => permissionCode.trim())
        .filter(Boolean);

    if (normalizedCodes.length === 0) {
        return false;
    }

    const currentPermissionCodes = getPermissionCodes(profile);
    if (normalizedCodes.some((permissionCode) => hasPermissionCode(currentPermissionCodes, permissionCode))) {
        return true;
    }

    return shouldUsePartFallback(currentPermissionCodes, options)
        ? hasLegacyPartAccess(profile, options)
        : false;
}

export function canSeeSection(
    profile: Profile | null | undefined,
    permissionCodes: PermissionCode[],
    fallbackParts: Part[] = [],
    options?: Omit<PermissionFallbackOptions, 'fallbackParts'>
): boolean {
    return hasAnyPermission(profile, permissionCodes, {
        ...options,
        fallbackParts,
        fallbackMode: options?.fallbackMode ?? DEFAULT_SECTION_FALLBACK_MODE,
    });
}

export function isSectionVisible(
    profile: Profile | null | undefined,
    config?: SectionVisibilityConfig | null
): boolean {
    if (!config) {
        return true;
    }

    const permissionCodes = config.permissionCodes?.map((permissionCode) => permissionCode.trim()).filter(Boolean) ?? [];
    const fallbackParts = [
        ...(config.fallbackPart ? [config.fallbackPart] : []),
        ...(config.fallbackParts ?? [])
    ].filter((part): part is Part => Boolean(part));

    if (permissionCodes.length > 0) {
        return hasAnyPermission(profile, permissionCodes, {
            fallbackPart: config.fallbackPart,
            fallbackParts: config.fallbackParts,
            fallbackMode: config.fallbackMode ?? DEFAULT_SECTION_FALLBACK_MODE,
        });
    }

    if (fallbackParts.length > 0) {
        return hasLegacyPartAccess(profile, {
            fallbackPart: config.fallbackPart,
            fallbackParts: config.fallbackParts,
        });
    }

    return true;
}

function shouldUsePartFallback(
    permissionCodes: Set<PermissionCode>,
    options?: PermissionFallbackOptions
): boolean {
    const fallbackMode = options?.fallbackMode ?? DEFAULT_PERMISSION_FALLBACK_MODE;

    if (fallbackMode === 'never') {
        return false;
    }

    if (fallbackMode === 'if-no-permission-codes') {
        return permissionCodes.size === 0;
    }

    return true;
}

function hasLegacyPartAccess(
    profile: Profile | null | undefined,
    options?: PermissionFallbackOptions
): boolean {
    if (!profile) {
        return false;
    }

    const fallbackParts = [
        ...(options?.fallbackPart ? [options.fallbackPart] : []),
        ...(options?.fallbackParts ?? [])
    ].filter((part): part is Part => Boolean(part));

    if (fallbackParts.length === 0) {
        return false;
    }

    return fallbackParts.some((part) => isVisible(profile, part));
}
